import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import commonActions from '../../../Actions/commonActions';
import priceSimulationActionsImp from '../../../Actions/priceSimulationActions';
import { priceSimulationActionTypes } from '../../../ActionTypes/priceSimulationActionTypes';
import Card from '../../../Components/Card/Card';
import AnalysisTabContent from '../../../Components/PriceSimulation/Beta/AnalysisTabContentBeta';
import '../../../Components/PriceSimulation/Beta/PriceSimulation.css';
import PriceSimulationWorksheet from '../../../Components/PriceSimulation/Beta/PriceSimulationWorksheetBeta';
import PricingFilterBar from '../../../Components/PriceSimulation/Beta/PricingFilterBarBeta';
import PricingSimulationHeader from '../../../Components/PriceSimulation/Beta/PricingSimulationHeaderBeta';
import { extractDraftModifications } from '../../../Components/PriceSimulation/Beta/proposalPayloadExtractor';
import SendPriceForApprovalModalBeta from '../../../Components/PriceSimulation/Beta/SendPriceForApprovalModalBeta';
import SimulationEmptyState from '../../../Components/PriceSimulation/Beta/SimulationEmptyStateBeta';
import SimulationTabContent from '../../../Components/PriceSimulation/Beta/SimulationTabContentBeta';
import { transformPackageToWorksheet } from '../../../Components/PriceSimulation/Beta/worksheetDataTransformerBeta';
import Tabs from '../../../Components/Tabs/Tabs';
import Modals from '../../../Constants/modals';
import { PriceSimulationStatus } from '../../../Constants/PriceSimulation/priceSimulationConstants';
import { VossProductTypeOptions } from '../../../Constants/VOSS/vossProductType';
import useDynamicPlanOptions from '../../../Hooks/Price-simulation/useDynamicPlanOptions';
import useModal from '../../../Hooks/useModal';

const PriceSimulationDetailContainer = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { openModal, isModalOpen } = useModal();
    const priceSimulationActions = bindActionCreators(priceSimulationActionsImp, dispatch);

    const packages = useSelector((state) => state.PriceSimulationReducer.packages);
    const packageDetailsMap = useSelector((state) => state.PriceSimulationReducer.packageDetailsMap);
    const packageMetadataMap = useSelector((state) => state.PriceSimulationReducer.packageMetadataMap);
    const isLoading = useSelector((state) => state.CommonReducer.dataLoading.packages);

    // Worksheet ref for accessing adjustment state
    const worksheetRef = useRef();

    // Tab state
    const [activeTab, setActiveTab] = useState({ id: 'simulation', label: t('priceSimulation.simulation') });

    // Package structure visibility
    const [packagesVisible, setPackagesVisible] = useState(true);
    const [expandedPackages, setExpandedPackages] = useState({});
    const [expandedTiers, setExpandedTiers] = useState({});

    // Filter state - arrays for multi-select
    const [packageBillingCycle, setPackageBillingCycle] = useState(12);
    const [productFilters, setProductFilters] = useState([]);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [selectedProductTypes, setSelectedProductTypes] = useState([]);
    const [selectedTiers, setSelectedTiers] = useState([]);
    const [selectedPlans, setSelectedPlans] = useState([]);
    const [modificationStatuses, setModificationStatuses] = useState([]);
    const [hasActiveProposal, setHasActiveProposal] = useState(false);
    const [isOutdatedVersion, setIsOutdatedVersion] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Dynamic plan options from selected packages
    const planOptions = useDynamicPlanOptions(selectedPackages);

    // Prune stale plan selections when available options change
    useEffect(() => {
        if (planOptions.length === 0) return;
        const validIds = new Set(planOptions.map(p => p.id));
        const pruned = selectedPlans.filter(p => validIds.has(p.id));
        if (pruned.length !== selectedPlans.length) {
            setSelectedPlans(pruned);
        }
    }, [planOptions]);

    // Resolve status option translations
    const modificationStatusOptions = useMemo(() =>
        PriceSimulationStatus.map(s => ({ ...s, name: t(s.nameKey) })),
    [t]);

    // Resolve product type option translations
    const productTypeOptions = useMemo(() =>
        VossProductTypeOptions.map(opt => ({ ...opt, name: t(opt.nameKey) })),
    [t]);

    // Generate package options from fetched packages
    const packageOptions = useMemo(() => {
        if (!packages || packages.length === 0) {
            return [];
        }
        return packages.map((pkg) => ({
            id: pkg.id,
            name: pkg.name || pkg.packageName || `Package ${pkg.id}`,
        }));
    }, [packages]);

    // Generate tier options from selected packages
    const tierOptions = useMemo(() => {
        if (selectedPackages.length === 0 || !packageDetailsMap || Object.keys(packageDetailsMap).length === 0) {
            return [];
        }
        
        const tierMap = new Map();
        selectedPackages.forEach((pkg) => {
            const packageData = packageDetailsMap[pkg.id];
            if (packageData?.tiers) {
                packageData.tiers.forEach((tier) => {
                    const id = tier.tierId || tier.id;
                    if (!tierMap.has(id)) {
                        tierMap.set(id, { id, name: tier.name });
                    }
                });
            }
        });
        
        return Array.from(tierMap.values());
    }, [selectedPackages, packageDetailsMap]);

    const tabs = [
        { id: 'simulation', label: t('priceSimulation.simulation') },
        { id: 'analysis', label: t('priceSimulation.analysis') },
    ];

    useEffect(() => {
        // If packages not loaded, fetch them
        if (!packages || packages.length === 0) {
            priceSimulationActions.getPackages();
        }
    }, []);

    // Fetch package details when packages are selected in filter
    useEffect(() => {
        if (selectedPackages.length > 0) {
            // Fetch details for each selected package that isn't already loaded
            selectedPackages.forEach((pkg) => {
                if (!packageDetailsMap[pkg.id]) {
                    priceSimulationActions.fetchPriceSimulationPackageContextBeta(pkg.id);
                }
            });
        }
    }, [selectedPackages, packageDetailsMap]);

    // Sync package metadata (draft/version status) to local state from first selected package
    useEffect(() => {
        if (selectedPackages.length > 0 && packageMetadataMap) {
            const metadata = packageMetadataMap[selectedPackages[0].id];
            if (metadata) {
                setHasActiveProposal(metadata.hasActiveProposal);
                setIsOutdatedVersion(metadata.isOutdatedVersion);
            }
        } else {
            setHasActiveProposal(false);
            setIsOutdatedVersion(false);
        }
    }, [selectedPackages, packageMetadataMap]);

    const handlePackageBillingChange = (months) => {
        setPackageBillingCycle(months);
    };

    // Transform API package structure to component format
    const transformPackageForDisplay = (apiPackage) => {
        if (!apiPackage?.tiers) return null;

        // Apply tier filter if selected
        const filteredTiers = selectedTiers.length > 0
            ? apiPackage.tiers.filter((tier) =>
                selectedTiers.some((st) => st.id === (tier.tierId || tier.id))
            )
            : apiPackage.tiers;

        if (filteredTiers.length === 0) return null;

        // Find the plan identifier matching the selected billing cycle
        const matchingPlan = (apiPackage.availablePlans || []).find((p) => {
            const months = extractDisplayMonths(p.identifier || p.name);
            return months === packageBillingCycle;
        }) || (apiPackage.availablePlans || [])[0];
        const planIdentifier = matchingPlan?.identifier || matchingPlan?.name;

        return {
            id: apiPackage.packageId || apiPackage.id,
            name: apiPackage.packageName || apiPackage.name,
            tiers: filteredTiers.map((tier) => {
                const tierId = tier.tierId || tier.id;
                return {
                    id: tierId,
                    name: tier.name,
                    periodMonths: packageBillingCycle,
                    productGroups: (tier.productGroups || []).map((group) => {
                        const groupId = group.groupId || group.id;
                        return {
                            id: groupId,
                            name: group.name,
                            productGroupType: group.status
                                ? (group.status.toLowerCase() === 'optional' ? 0 : 1)
                                : 1,
                            products: (group.products || []).map((product) => {
                                const productId = product.productId || product.id;
                                const pricePoint = planIdentifier ? product.prices?.[planIdentifier] : null;
                                return {
                                    id: productId,
                                    name: product.name,
                                    price: pricePoint?.currentPrice ?? 0,
                                    productType: product.type,
                                    isModified: pricePoint?.isModified ?? false,
                                };
                            }),
                        };
                    }),
                };
            }),
        };
    };

    // Helper: extract months from plan identifier like "Monthly (1M)" → 1
    const extractDisplayMonths = (planIdentifier) => {
        // Match "Monthly (1M)" or bare "1M", "3M", "12M"
        const wrapped = planIdentifier?.match(/\((\d+)M\)/i);
        if (wrapped) return parseInt(wrapped[1], 10);
        const bare = planIdentifier?.match(/^(\d+)M$/i);
        if (bare) return parseInt(bare[1], 10);
        return 1;
    };

    // Use transformed selected packages
    const displayPackages = useMemo(() => {
        if (selectedPackages.length > 0) {
            return selectedPackages
                .map((pkg) => {
                    const details = packageDetailsMap[pkg.id];
                    return details ? transformPackageForDisplay(details) : null;
                })
                .filter(Boolean);
        }
        return [];
    }, [selectedPackages, packageDetailsMap, packageBillingCycle, selectedTiers]);

    // Worksheet data: transform all selected packages
    const worksheetPackages = useMemo(() => {
        return selectedPackages
            .map((pkg) => {
                const details = packageDetailsMap[pkg.id];
                return details ? transformPackageToWorksheet(details, pkg.id, packageBillingCycle, selectedTiers) : null;
            })
            .filter(Boolean);
    }, [selectedPackages, packageDetailsMap, packageBillingCycle, selectedTiers]);

    const handleTogglePackage = (packageId) => {
        setExpandedPackages((prev) => ({ ...prev, [packageId]: !prev[packageId] }));
    };

    const handleToggleTier = (tierId) => {
        setExpandedTiers((prev) => ({ ...prev, [tierId]: !prev[tierId] }));
    };

    const handleTogglePackagesVisibility = () => {
        setPackagesVisible((prev) => !prev);
    };

    // Auto-expand packages and tiers that have matches
    useEffect(() => {
        const newExpandedPackages = {};
        const newExpandedTiers = {};

        displayPackages.forEach((pkg) => {
            newExpandedPackages[pkg.id] = true;
            pkg.tiers.forEach((tier) => {
                newExpandedTiers[tier.id] = true;
            });
        });

        setExpandedPackages(newExpandedPackages);
        setExpandedTiers(newExpandedTiers);
    }, [displayPackages]);

    const handleSaveDraft = useCallback(async () => {
        const adjustments = worksheetRef.current?.getAdjustments() ?? {};
        const packageIds = selectedPackages.map(p => p.id);
        const payloads = extractDraftModifications(packageDetailsMap, packageIds, adjustments);

        if (payloads.length === 0) {
            dispatch(commonActions.showWarning(t('priceSimulation.noChangesToSave')));
            return;
        }

        setIsSaving(true);
        try {
            await priceSimulationActions.saveDraftProposals(payloads);
            worksheetRef.current?.resetAdjustments();
            dispatch({ type: priceSimulationActionTypes.INVALIDATE_PACKAGE_CONTEXTS, packageIds });
            dispatch(commonActions.showSuccess(t('priceSimulation.draftSaved')));
        } catch {
            dispatch(commonActions.showError(t('priceSimulation.draftSaveFailed')));
        } finally {
            setIsSaving(false);
        }
    }, [selectedPackages, packageDetailsMap, dispatch, t, priceSimulationActions]);

    return (
        <div className="price-simulation-detail">
            <PricingSimulationHeader onSaveDraft={handleSaveDraft} isSaving={isSaving} onSendForApproval={() => openModal(Modals.SendPriceForApprovalModal)} />

            {isOutdatedVersion && (
                <div className="outdated-version-banner">
                    <span className="vismaicon vismaicon-sm vismaicon-warning"></span>
                    <span>{t('priceSimulation.outdatedVersionWarning')}</span>
                </div>
            )}

            <Card>
                <Card.Content isLoading={isLoading}>
                    <PricingFilterBar
                        productFilters={productFilters}
                        onProductFiltersChange={setProductFilters}
                        selectedPackages={selectedPackages}
                        onPackagesChange={setSelectedPackages}
                        selectedProductTypes={selectedProductTypes}
                        onProductTypesChange={setSelectedProductTypes}
                        selectedTiers={selectedTiers}
                        onTiersChange={setSelectedTiers}
                        selectedPlans={selectedPlans}
                        onPlansChange={setSelectedPlans}
                        modificationStatuses={modificationStatuses}
                        onModificationStatusesChange={setModificationStatuses}
                        productOptions={[]}
                        packageOptions={packageOptions}
                        productTypeOptions={productTypeOptions}
                        tierOptions={tierOptions}
                        planOptions={planOptions}
                        modificationStatusOptions={modificationStatusOptions}
                    />
                </Card.Content>
            </Card>

            <div className="simulation-tabs-container" style={{ marginTop: '24px' }}>
                <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

                <div className="">
                    {activeTab?.id === 'simulation' && (
                        selectedPackages.length === 0 ? (
                            <SimulationEmptyState />
                        ) : (
                            <>
                                <SimulationTabContent
                                    packages={displayPackages}
                                    expandedPackages={expandedPackages}
                                    expandedTiers={expandedTiers}
                                    onTogglePackage={handleTogglePackage}
                                    onToggleTier={handleToggleTier}
                                    isVisible={packagesVisible}
                                    onToggleVisibility={handleTogglePackagesVisibility}
                                    selectedPeriod={packageBillingCycle}
                                    onPeriodChange={handlePackageBillingChange}
                                />
                                
                                <PriceSimulationWorksheet
                                    ref={worksheetRef}
                                    selectedPackages={worksheetPackages}
                                    modificationStatuses={modificationStatuses}
                                    selectedPlans={selectedPlans}
                                    selectedProductTypes={selectedProductTypes}
                                />
                            </>
                        )
                    )}

                    {activeTab?.id === 'analysis' && <AnalysisTabContent />}
                </div>
            </div>

            {isModalOpen(Modals.SendPriceForApprovalModal) && (
                <SendPriceForApprovalModalBeta
                    adjustments={worksheetRef.current?.getAdjustments() ?? {}}
                    selectedPackageIds={selectedPackages.map(p => p.id)}
                />
            )}
        </div>
    );
};

export default PriceSimulationDetailContainer;
